import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
} from '@angular/core';

@Directive({
  selector: '[customContainer]',
  standalone: true,
})
export class CustomContainerDirective implements AfterViewInit {
  backgroundColor = input<string>();
  borderColor = input<string>();

  private el = inject(ElementRef<HTMLElement>);
  private destroyRef = inject(DestroyRef);
  private debounceTimer?: ReturnType<typeof setTimeout>;
  private immediateTimer?: ReturnType<typeof setTimeout>;
  private observer?: MutationObserver;

  constructor() {
    this.destroyRef.onDestroy(() => {
      clearTimeout(this.debounceTimer);
      clearTimeout(this.immediateTimer);
      this.observer?.disconnect();
    });
  }

  ngAfterViewInit(): void {
    this.applyStyles();
  }

  @HostListener('window:resize')
  onResize(): void {
    // Immediate pass: handles standard drag-resize events
    clearTimeout(this.immediateTimer);
    this.immediateTimer = setTimeout(() => this.applyStylesDirect(), 0);
    // Debounced delayed pass: handles maximize/minimize where GoAB's Svelte
    // component re-renders its shadow DOM asynchronously after the resize event
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.applyStylesDirect(), 200);
  }

  private applyStyles(): void {
    clearTimeout(this.immediateTimer);
    this.applyWithRetry(0);
  }

  private applyWithRetry(attempt: number): void {
    const MAX_ATTEMPTS = 10;
    const RETRY_DELAY = 50;

    if (attempt >= MAX_ATTEMPTS) return;

    this.immediateTimer = setTimeout(
      () => {
        const applied = this.applyStylesDirect();
        if (!applied) {
          this.applyWithRetry(attempt + 1);
        }
      },
      attempt === 0 ? 50 : RETRY_DELAY
    );
  }

  private applyStylesDirect(): boolean {
    const goaContainer = this.el.nativeElement.querySelector('goa-container');
    if (!goaContainer) return false;

    goaContainer.removeAttribute('type');

    const shadowRoot = goaContainer.shadowRoot;
    if (!shadowRoot) return false;

    const containerRoot = shadowRoot.querySelector('div') as HTMLElement;
    if (!containerRoot) return false;

    const bgColor = this.backgroundColor() || 'transparent';
    const bColor = this.borderColor() || 'transparent';

    containerRoot.style.setProperty('background-color', bgColor, 'important');
    containerRoot.style.setProperty('border-color', bColor, 'important');
    this.applyBorderColor(shadowRoot, bColor);

    this.observeShadowRoot(shadowRoot);
    return true;
  }

  // The visible border is actually drawn on the header/content elements
  // (via the --goa-container-border shorthand), not on the outer wrapper div,
  // so border-color must be overridden there too to take effect.
  private applyBorderColor(shadowRoot: ShadowRoot, bColor: string): void {
    const header = shadowRoot.querySelector('header') as HTMLElement | null;
    const content = shadowRoot.querySelector('.content') as HTMLElement | null;
    header?.style.setProperty('border-color', bColor, 'important');
    content?.style.setProperty('border-color', bColor, 'important');
  }

  private observeShadowRoot(shadowRoot: ShadowRoot): void {
    if (this.observer) return;

    this.observer = new MutationObserver(() => {
      const containerRoot = shadowRoot.querySelector('div') as HTMLElement;
      if (!containerRoot) return;

      const bgColor = this.backgroundColor() || 'transparent';
      const bColor = this.borderColor() || 'transparent';

      // Guard: skip if already correct to avoid retriggering the observer
      if (
        containerRoot.style.getPropertyValue('background-color') === bgColor &&
        containerRoot.style.getPropertyValue('border-color') === bColor
      ) {
        return;
      }

      containerRoot.style.setProperty('background-color', bgColor, 'important');
      containerRoot.style.setProperty('border-color', bColor, 'important');
      this.applyBorderColor(shadowRoot, bColor);
    });

    this.observer.observe(shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }
}
